import { getPharmacySecureId } from '@develop/ia-api-library'
import type { HttpResponse } from '@develop/ia-api-library'
import { convertMinutesToMilliseconds } from '@develop/ia-utils'
import { defineStore } from 'pinia'
import { fetchPharmacyDetails } from '../utils/fetch-pharmacy-details'
import { DefaultChannelId } from '~/models/pharmacy'
import type { PharmacyDetails } from '@/models/pharmacy-details'
import { LocalStorage } from '~/models'
import { Classification } from '~/models/generated/iade'

export const useCurrentPharmacyStore = defineStore('current-pharmacy', () => {
  const $pharmacy = ref<PharmacyDetails | null>(null)
  const pharmacySecureId = ref<string | null | undefined>(null)

  const pharmacyId = computed(() => $pharmacy.value?.id)
  const isPharmacySet = computed(() => isDefined(pharmacyId))

  // Required for legacy FEC --> https://sbentwicklung.atlassian.net/browse/IBT-14360?focusedCommentId=123070
  const pharmacyLocalStorage = useLocalStorage(LocalStorage.SelectedPharmacyData, {
    pharmacyData: $pharmacy.value,
    saveTime: 0,
  })

  const deliverySettings = computed(() => {
    return $pharmacy.value?.delivery
  })

  // on web only Paypal is possible
  const hasPayment = computed(() => {
    return $pharmacy.value?.additionalInformation.allowsPayments
  })

  const hasWhatsApp = computed(() => {
    return $pharmacy.value?.classifications.includes(Classification.Pcase35) && !!$pharmacy.value?.contact.whatsAppMobilePhoneNumber.length
  })

  const homeDeliveryZipCodes = computed(() => {
    const deliveryLimit = deliverySettings.value?.standard?.deliveryLimits?.zipCodes ?? null
    return deliveryLimit ? deliveryLimit.sort() : []
  })

  const expressDeliveryZipCodes = computed(() => {
    const deliveryLimit = deliverySettings.value?.express?.deliveryLimits?.zipCodes ?? null
    return deliveryLimit ? deliveryLimit.sort() : []
  })

  const hasPrescriptionDelivery = computed(() => deliverySettings.value?.standard?.prescriptionDelivery)

  const isDeactivated = computed(() => {
    const iaChannel = $pharmacy.value?.channels.find(channel => channel.id === DefaultChannelId)
    return iaChannel ? !iaChannel.linkToProfile : true
  })

  const slugSegments = computed(() => {
    if (!$pharmacy.value) {
      return undefined
    }
    return convertPharmacySlugToParams($pharmacy.value.additionalInformation.slug)
  })

  const fullAddress = computed(() => isDefined($pharmacy) ? `${$pharmacy.value.location.street} ${$pharmacy.value.location.postcode} ${$pharmacy.value.location.city}` : '')

  const resetPharmacy = () => set($pharmacy, null)

  const fetchPharmacy = async (id: number) => {
    const load = async () => {
      // TODO: rework it using useAsyncData
      // this will also also fix problems throughout the app
      const response = await fetchPharmacyDetails(id)

      if (!response.success) {
        onShowLogs(() => console.error('Error [getPharmacyDetails]', response.message))
      } else {
        // graphql delivers string for pharmacy id
        set($pharmacy, response.data)
      }

      return Promise.resolve<HttpResponse<PharmacyDetails>>(response)
    }

    return await load()
  }

  const setPharmacy = async (id: number) => {
    const response = await fetchPharmacy(id)

    // Needed for legacy FEC, remove after migration
    setLocalStoragePharmacy(response.data)
    return response
  }

  // Needed for legacy FEC, remove after migration
  const setLocalStoragePharmacy = (pharmacyData: PharmacyDetails | null) => {
    if (!pharmacyData)
      return

    const EXPIRATION_TIME_IN_MINUTES = 30
    const expirationTime = convertMinutesToMilliseconds(EXPIRATION_TIME_IN_MINUTES)
    const saveTime = new Date().getTime() + expirationTime
    pharmacyLocalStorage.value = {
      pharmacyData,
      saveTime,
    }
  }

  const fetchPharmacySecureId = async () => useAsyncData(async () => {
    const { data } = await getPharmacySecureId(pharmacyId.value!)

    pharmacySecureId.value = data?.secureId
  })

  return {
    pharmacy: $pharmacy,
    pharmacyId,
    slugSegments,
    isPharmacySet,
    isDeactivated,
    deliverySettings,
    hasPayment,
    hasWhatsApp,
    fullAddress,
    ensure: setPharmacy,
    reset: resetPharmacy,
    fetchPharmacy,
    homeDeliveryZipCodes,
    expressDeliveryZipCodes,
    hasPrescriptionDelivery,
    setLocalStoragePharmacy,
    fetchPharmacySecureId,
    pharmacySecureId,
  }
})
